// src/SideWinder.Web/src/composables/useVisibleFunds.ts
//
// Japan fund page UI Task 3 (spec 2026-09-28-japan-fund-page-ui): the
// registry-driven fund-visibility composable for nav-level surfaces (which
// portfolio pills/slugs a user may navigate to).
//
// Fix round 1 (2026-09-28 review): this used to compute its own visibility
// rule off requiresEntitlement directly ("show every non-gated fund
// unconditionally"), which disagreed with the already-shipped Phase 2 rule
// (isFundVisibleFor: once /me has loaded, it's an EXACT filter — a
// non-gated fund missing from entitledFunds is hidden too). Two rules for
// the same question is exactly the kind of drift this task exists to avoid,
// so useVisibleFunds now delegates to isFundVisibleFor directly — one rule,
// shared by useFundEnablement's isFundVisible (per-fund data filtering) and
// this composable (per-portfolio nav visibility) alike.

import { computed } from 'vue'
import { useFundEnablement } from './useFundEnablement'
import { FUND_REGISTRY, REGISTERED_PORTFOLIOS, isFundVisibleFor, isLiveFundAccessibleFor, type Fund, type PortfolioSlug } from '../components/nav/fundConfig'

export function useVisibleFunds() {
  const {
    entitledFunds: entitledRaw, viewableFunds: viewableRaw, accessibleFunds: accessibleRaw,
  } = useFundEnablement()
  // Superuser view-only access (2026-09-28): nav visibility follows /me's
  // viewableFunds when present (a superuser previews gated funds without a
  // grant), else entitledFunds — still through the one shared
  // isFundVisibleFor rule, so null (not loaded / error) ⇒ gated hidden.
  // (`viewableRaw?.` / `accessibleRaw?.` tolerate test doubles of
  // useFundEnablement that predate the fields.)
  const visibilitySource = computed<string[] | null>(() => viewableRaw?.value ?? entitledRaw.value)
  // Spec 2026-09-30: live funds the admin switched off are hidden from every nav surface
  // (portfolio pills, NavPanel). null ⇒ fail OPEN.
  const accessSource = computed<string[] | null>(() => accessibleRaw?.value ?? null)
  const visiblePortfolios = computed<PortfolioSlug[]>(() =>
    REGISTERED_PORTFOLIOS.filter(s =>
      isFundVisibleFor(visibilitySource.value, FUND_REGISTRY[s].fund)
      && isLiveFundAccessibleFor(accessSource.value, FUND_REGISTRY[s].fund)))
  const visibleFunds = computed<Fund[]>(() => visiblePortfolios.value.map(s => FUND_REGISTRY[s].fund))
  /** Live portfolios restricted for this user — usePortfolio sends their hostnames to BGA. */
  const blockedPortfolios = computed<PortfolioSlug[]>(() =>
    REGISTERED_PORTFOLIOS.filter(s => !isLiveFundAccessibleFor(accessSource.value, FUND_REGISTRY[s].fund)))
  const isVisible = (slug: PortfolioSlug) => visiblePortfolios.value.includes(slug)
  /** View-only preview: the caller can see this fund but is NOT entitled to
   *  it (e.g. a superuser on Japan without a grant — no dealing-plan legs). */
  const isPreview = (slug: PortfolioSlug) =>
    isVisible(slug) && !isFundVisibleFor(entitledRaw.value, FUND_REGISTRY[slug].fund)
  return { visiblePortfolios, visibleFunds, blockedPortfolios, isVisible, isPreview }
}
